import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Formik } from 'formik';
import omit from 'lodash.omit';

import Button from '../Button';
import { getNativeUsaStates } from '../../../util/usa-states';
import { splitToFirstLastName } from '../../../util/text';
import getConfig from '../../../util/config';
import Alert from '../Alert';
import { splitDate } from './helpers';
import { convertStateToAbbreviation } from '../../../apps/pro/views/AccountAddressBook/components/AddressBookCSVUpload/components/ColumnMatcher/helpers';

const propTypes = {
  className: PropTypes.string,
  address: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    phone_number: PropTypes.string,
    email_address: PropTypes.string,
    date_of_birth: PropTypes.string,
    address_line_1: PropTypes.string,
    address_line_2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    post_code: PropTypes.string,
    country: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

const ExternalShippingAddressForm = ({
  className,
  address,
  onSubmit,
  loading,
  error,
}) => {
  const classes = classNames({
    'external-shipping-address-form-wrap': true,
    'is-loading': loading,
    [className]: !!className,
  });
  const config = getConfig();

  const onFormSubmit = values => {
    const {
      date_of_birth_day: day,
      date_of_birth_month: month,
      date_of_birth_year: year,
    } = values;

    onSubmit({
      ...address,
      ...omit(values, [
        'first_name',
        'last_name',
        'date_of_birth_year',
        'date_of_birth_month',
        'date_of_birth_day',
      ]),
      name: `${values.first_name.trim()} ${values.last_name.trim()}`,
      date_of_birth: day && month ? `${year}-${month}-${day}` : null,
    });
  };

  const { firstName, lastName } = splitToFirstLastName(address?.name ?? '');
  const date = splitDate(address?.date_of_birth);

  return (
    <div className={classes}>
      <Formik
        initialValues={{
          first_name: firstName || '',
          last_name: lastName || '',
          phone_number: address?.phone_number || '',
          address_line_1: address?.address_line_1 || '',
          address_line_2: address?.address_line_2 || '',
          city: address?.city || '',
          state: address?.state || '',
          post_code: address?.post_code || '',
          country_id:
            address?.country?.id ||
            address?.country_id ||
            config?.country?.usa?.id,
          date_of_birth_year: date.year || '0001', // Just a far in the past year.
          date_of_birth_month: date.month || '',
          date_of_birth_day: date.day || '',
        }}
        onSubmit={onFormSubmit}
      >
        {({ values, handleChange, handleBlur, handleSubmit }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className="external-shipping-address-form"
            >
              <div className="external-shipping-address-form-inputs">
                <label htmlFor="first_name" className="form-group-inline">
                  <span className="form-group-label">First Name</span>
                  <input
                    className="form-group-control"
                    type="text"
                    name="first_name"
                    id="first_name"
                    value={values.first_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                </label>

                <label htmlFor="last_name" className="form-group-inline">
                  <span className="form-group-label">Last Name</span>
                  <input
                    className="form-group-control"
                    type="text"
                    name="last_name"
                    id="last_name"
                    value={values.last_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                </label>

                <label htmlFor="address_line_1" className="form-group-inline">
                  <span className="form-group-label">Address Line 1</span>
                  <input
                    className="form-group-control"
                    type="text"
                    name="address_line_1"
                    id="address_line_1"
                    value={values.address_line_1}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                </label>

                <label htmlFor="address_line_2" className="form-group-inline">
                  <span className="form-group-label">Address Line 2</span>
                  <input
                    className="form-group-control"
                    type="text"
                    name="address_line_2"
                    id="address_line_2"
                    value={values.address_line_2}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </label>

                <label htmlFor="post_code" className="form-group-inline">
                  <span className="form-group-label">Postal Code</span>
                  <input
                    className="form-group-control"
                    type="text"
                    name="post_code"
                    id="post_code"
                    value={values.post_code}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                </label>

                <label htmlFor="city" className="form-group-inline">
                  <span className="form-group-label">City</span>
                  <input
                    className="form-group-control"
                    type="text"
                    name="city"
                    id="city"
                    value={values.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                </label>

                <label htmlFor="state" className="form-group-inline">
                  <span className="form-group-label">State</span>
                  <select
                    className="form-group-control"
                    name="state"
                    id="state"
                    value={convertStateToAbbreviation(values.state)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  >
                    <option value="">Select a state&hellip;</option>
                    {getNativeUsaStates().map(state => {
                      return (
                        <option
                          key={state.abbreviation}
                          value={state.abbreviation}
                        >
                          {state.name} &ndash; {state.abbreviation}
                        </option>
                      );
                    })}
                  </select>
                </label>

                <label htmlFor="country" className="form-group-inline">
                  <span className="form-group-label">Country</span>
                  <input
                    className="form-group-control"
                    type="text"
                    name="country"
                    id="country"
                    defaultValue={
                      address?.country?.id
                        ? address.country.name
                        : config.country.usa.name
                    }
                    readOnly
                    disabled
                  />
                </label>

                <label htmlFor="phone_number" className="form-group-inline">
                  <span className="form-group-label">Mobile Phone</span>
                  <input
                    className="form-group-control"
                    type="text"
                    name="phone_number"
                    id="phone_number"
                    value={values.phone_number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </label>

                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="form-group-inline">
                  <span className="form-group-label">Birthday</span>
                  <select
                    className="form-group-control"
                    name="date_of_birth_month"
                    id="date_of_birth_month"
                    value={values.date_of_birth_month}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">Month</option>
                    <option value="01">January</option>
                    <option value="02">February</option>
                    <option value="03">March</option>
                    <option value="04">April</option>
                    <option value="05">May</option>
                    <option value="06">June</option>
                    <option value="07">July</option>
                    <option value="08">August</option>
                    <option value="09">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                  <select
                    className="form-group-control"
                    name="date_of_birth_day"
                    id="date_of_birth_day"
                    value={values.date_of_birth_day}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, index) => index + 1).map(
                      x => {
                        const value = String(x).padStart(2, '0');
                        return (
                          <option key={x} value={value}>
                            {value}
                          </option>
                        );
                      },
                    )}
                  </select>
                </label>
              </div>

              {error && (
                <Alert type="danger" className="my-2">
                  {error}
                </Alert>
              )}

              <Button type="submit" btnBlock btnSize="lg" disabled={loading}>
                {loading ? 'Please wait...' : 'Save'}
              </Button>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

ExternalShippingAddressForm.propTypes = propTypes;

export default ExternalShippingAddressForm;
